// @flow
import React, { Children, type Node } from 'react';
import classnames from 'classnames';

import { COLORS } from 'Components/utils/color/colors';

import { themes } from './StripButton.themes';

type StripButtonProps = {
  children: Node,
  className?: string,
  color?: Colors,
  colorBorder?: Colors,
  dataTestId?: string,
  height?: number,
  onClick?: () => mixed,
  orientation?: 'top' | 'bottom',
  theme?: string,
};

function StripButtonSvg({
  className,
  color,
  colorBorder,
  height,
  hasBottomOrientation,
}: {
  className?: string,
  color: Colors,
  colorBorder: Colors,
  height: number,
  hasBottomOrientation: boolean,
}) {
  return (
    <svg
      className={classnames(className, 'fill-current', `text-${color}`)}
      x="0px"
      y="0px"
      viewBox="0 0 15 30"
      height={height}
      width={30}
      style={{ flexBasis: '15px' }}
    >
      <g>
        <g transform={`translate(-61.000000, ${hasBottomOrientation ? -155.5 : -156.5})`}>
          <g transform="translate(66.000000, 166.000000)">
            <g>
              <g>
                <path d="M10,21H-5L2.8-3.6C3.9-6.8,6.8-9,10-9" />
              </g>
            </g>
          </g>
        </g>
      </g>
      <g>
        <g transform={`translate(-61.000000, ${hasBottomOrientation ? -155.5 : -156.5})`}>
          <g transform="translate(66.000000, 166.000000)">
            <g>
              <g>
                <path
                  style={{ strokeWidth: '1px' }}
                  className={`fill-transparent stroke-current text-${colorBorder}`}
                  d="M-5,21L2.8-3.6C3.9-6.8,6.8-9,10-9"
                />
              </g>
            </g>
          </g>
        </g>
      </g>
    </svg>
  );
}

export function StripButton(props: StripButtonProps) {
  const {
    children,
    className,
    color = COLORS.WHITE,
    colorBorder = COLORS.GRAY_LIGHT,
    dataTestId = 'strip-button',
    height = 30,
    onClick,
    orientation = 'top',
    theme = 'default',
  } = props;

  if (!Children.count(children)) return null;

  const { thInner, thRoot } = themes[theme]({ color, colorBorder, orientation });

  const hasBottomOrientation = orientation === 'bottom';
  const svgProps = { color, colorBorder, height, hasBottomOrientation };

  return (
    <button
      className={classnames(className, thRoot)}
      onClick={onClick}
      type="button"
      data-testid={dataTestId}
    >
      <StripButtonSvg
        className={hasBottomOrientation ? '-scale-y-1 rtl:-scale-y-1' : 'rtl:-scale-x-1'}
        {...svgProps}
      />
      <span className={thInner} style={{ height }}>
        {children}
      </span>
      <StripButtonSvg
        className={hasBottomOrientation ? '-scale-1 rtl:-scale-1' : '-scale-x-1 rtl:translate-x-0'}
        {...svgProps}
      />
    </button>
  );
}
